// apps/web/src/pages/planejamento/fila-page.tsx — Fase 03, Plano 05 (OPS-04, D-02).
import React, { useEffect, useState } from 'react';
import {
  Layers,
  ArrowUp,
  ArrowDown,
  Lock,
  GripVertical,
  Hand,
  RefreshCw,
  AlertCircle,
  Filter,
} from 'lucide-react';
import type { FilaPlanejamentoItem } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import { useToast } from '../../components/ui/toast';
import { CampoSelect } from '../../components/ui/campo-select';

function mensagemDaResposta(valor: unknown, padrao: string): string {
  if (typeof valor !== 'object' || valor === null) return padrao;
  const mensagem = (valor as Record<string, unknown>).message;
  return typeof mensagem === 'string' ? mensagem : padrao;
}

function ehItemFila(valor: unknown): valor is FilaPlanejamentoItem {
  return typeof valor === 'object' && valor !== null && typeof (valor as Record<string, unknown>).opId === 'string';
}

function itensFila(valor: unknown): FilaPlanejamentoItem[] {
  return Array.isArray(valor) ? valor.filter(ehItemFila) : [];
}

function celulasDaResposta(valor: unknown): Array<{ codigo: string; nome: string }> {
  const dados = typeof valor === 'object' && valor !== null ? (valor as Record<string, unknown>).data ?? valor : valor;
  if (!Array.isArray(dados)) return [];
  return dados.flatMap((celula) => {
    if (typeof celula !== 'object' || celula === null) return [];
    const item = celula as Record<string, unknown>;
    return typeof item.codigo === 'string' && typeof item.nome === 'string' ? [{ codigo: item.codigo, nome: item.nome }] : [];
  });
}

export const PlanejamentoFilaPage: React.FC = () => {
  const toast = useToast();
  const [fila, setFila] = useState<FilaPlanejamentoItem[]>([]);
  const [celulas, setCelulas] = useState<Array<{ codigo: string; nome: string }>>([]);
  const [celulaSelecionada, setCelulaSelecionada] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const carregarDados = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const url = celulaSelecionada
        ? `/api/v1/planejamento/fila?celulaId=${encodeURIComponent(celulaSelecionada)}`
        : '/api/v1/planejamento/fila';

      const [resFila, resCelulas] = await Promise.all([
        fetch(url),
        fetch('/api/v1/planejamento/celulas'),
      ]);

      if (resFila.ok) {
        const dadosFila: unknown = await resFila.json();
        setFila(itensFila(dadosFila));
      }

      if (resCelulas.ok) {
        const dadosCelulas: unknown = await resCelulas.json();
        setCelulas(celulasDaResposta(dadosCelulas));
      }
    } catch {
      setErrorMsg('Falha ao carregar fila de produção');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarDados();
  }, [celulaSelecionada]);

  const handleMover = async (index: number, direcao: 'up' | 'down') => {
    const targetIndex = direcao === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= fila.length) return;

    const item = fila[index];
    const targetItem = fila[targetIndex];
    if (!item || !targetItem) return;

    // D-02 trava a OP inteira só sem célula. Com célula, a etapa corrente segue reordenável (D-13).
    if (item.iniciada && !celulaSelecionada) {
      alert('OP em execução — a posição não pode ser alterada (D-02).');
      return;
    }

    const previousFila = [...fila];
    const novaFila = [...fila];
    novaFila[index] = targetItem;
    novaFila[targetIndex] = item;

    // Atualização otimista
    setFila(novaFila);
    try {
      const res = await apiMutate(`/api/v1/planejamento/fila/${item.opId}/posicao`, {
        method: 'PATCH',
        body: {
          novaPosicao: targetIndex + 1,
          ...(celulaSelecionada ? { celulaId: celulaSelecionada } : {}),
        },
      });

      if (!res.ok) {
        const errJson: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(errJson, 'Erro ao reordenar fila'));
      }
      toast.success({
        title: 'Fila reordenada.',
        action: { label: 'Desfazer', onClick: () => { void handleDesfazer(previousFila, item.opId, index + 1, celulaSelecionada || undefined); } },
      });
    } catch {
      // Reverte em caso de erro
      setFila(previousFila);
      setErrorMsg('Não foi possível salvar a nova ordem. A fila voltou ao estado anterior — tente novamente.');
    }
  };

  const handleDesfazer = async (prev: FilaPlanejamentoItem[], opId: string, posicaoAnterior: number, celulaId?: string) => {

    // WR-06 (03-REVIEW.md): "Desfazer" precisa reverter a posição no backend —
    // a reordenação já foi persistida via PATCH quando o toast apareceu, então
    // apenas trocar o estado local e recarregar mostrava a posição nova de volta.
    try {
      const res = await apiMutate(`/api/v1/planejamento/fila/${opId}/posicao`, {
        method: 'PATCH',
        body: {
          novaPosicao: posicaoAnterior,
          ...(celulaId ? { celulaId } : {}),
        },
      });

      if (!res.ok) {
        const errJson: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(errJson, 'Erro ao desfazer reordenação'));
      }

      setFila(prev);
      toast.info({ title: 'Ação desfeita.', duration: 4000 });
    } catch {
      setErrorMsg('Não foi possível desfazer a reordenação. Tente novamente.');
      toast.error({ title: 'Não foi possível desfazer a reordenação.', description: 'Tente novamente.' });
    } finally {
      void carregarDados();
    }
  };

  const contagemTexto = `${fila.length} ${fila.length === 1 ? 'OP' : 'OPs'}`;

  return (
    <div className="flex flex-col h-full space-y-4 p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Layers className="w-6 h-6 text-slate-800" />
            Fila de Planejamento de Produção
          </h1>
          <p className="text-sm text-slate-500">
            Organize prioridades e acompanhe as exceções de planejamento.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 font-mono">{contagemTexto}</span>
          <button
            onClick={() => void carregarDados()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Filtro de Célula */}
      <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg border text-xs">
        <Filter className="w-4 h-4 text-slate-500" />
        <span className="font-semibold text-slate-700">Filtrar por Célula:</span>
        <CampoSelect
          aria-label="Filtrar por célula"
          size="sm"
          value={celulaSelecionada}
          onValueChange={setCelulaSelecionada}
          options={[{ value: '', label: 'Todas as Células' }, ...celulas.map((c) => ({ value: c.codigo, label: `${c.nome} (${c.codigo})` }))]}
          className="w-64 text-xs text-slate-800"
        />
      </div>

      {/* Alerta de Erro */}
      {errorMsg && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Grade D5 da Fila */}
      <div className="border rounded-lg overflow-hidden bg-white shadow-xs">
        {loading ? (
          <div className="p-6 space-y-2">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />
            ))}
          </div>
        ) : fila.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-xs">
            Nenhuma OP na fila de produção.
          </div>
        ) : (
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
                <th className="py-2.5 px-3 w-16 text-center">Pos.</th>
                {celulaSelecionada ? (
                  <th className="py-2.5 px-3 text-center">Posição no Kanban</th>
                ) : null}
                <th className="py-2.5 px-3">OP / Cliente</th>
                <th className="py-2.5 px-3">Material / Lote</th>
                <th className="py-2.5 px-3 text-center">Qtd Planejada</th>
                <th className="py-2.5 px-3 text-center">Prioridade</th>
                <th className="py-2.5 px-3 text-center">Estado</th>
                <th className="py-2.5 px-3 text-right">Reordenação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fila.map((item, index) => {
                const temPosManual = item.filaPosicaoManual !== null && item.filaPosicaoManual !== undefined;
                return (
                  <tr
                    key={item.opId}
                    className={`transition-colors ${
                      item.iniciada ? 'bg-slate-50/50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="py-2.5 px-3 text-center font-mono font-bold text-slate-800">
                      <div className="flex items-center justify-center gap-1">
                        {item.iniciada ? (
                          <span title="OP em execução — a posição não pode ser alterada.">
                            <Lock className="w-3.5 h-3.5 text-slate-400" />
                          </span>
                        ) : (
                          <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                        )}
                        <span>{index + 1}</span>
                      </div>
                    </td>

                    {celulaSelecionada ? (
                      <td className="py-2.5 px-3 text-center font-mono font-semibold text-slate-800">
                        {item.posicaoKanban ?? '—'}
                      </td>
                    ) : null}

                    <td className="py-2.5 px-3">
                      <div className="flex flex-col">
                        <span className="font-mono font-semibold text-slate-900">{item.opNumero}</span>
                        <span className="text-[11px] text-slate-500 truncate max-w-xs">{item.clienteNome}</span>
                      </div>
                    </td>

                    <td className="py-2.5 px-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-800 truncate max-w-xs">{item.materialNome}</span>
                        <span className="text-[11px] font-mono text-slate-500">Lote: {item.loteNumero}</span>
                      </div>
                    </td>

                    <td className="py-2.5 px-3 text-center font-mono font-semibold text-slate-800">
                      {item.quantidadePlanejada}
                    </td>

                    <td className="py-2.5 px-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono bg-slate-100 text-slate-700">
                        {item.prioridade}
                        {temPosManual && (
                          <span title="Ordem manual aplicada">
                            <Hand className="w-3 h-3 text-amber-600" />
                          </span>
                        )}
                      </span>
                    </td>

                    <td className="py-2.5 px-3 text-center">
                      {item.iniciada ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          Em Produção
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border">
                          Fila / Aguardando
                        </span>
                      )}
                    </td>

                    <td className="py-2.5 px-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => void handleMover(index, 'up')}
                          disabled={index === 0 || (item.iniciada && !celulaSelecionada)}
                          aria-label={item.iniciada && !celulaSelecionada ? 'OP em execução — a posição não pode ser alterada.' : 'Mover para cima'}
                          className="p-1 border rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-700"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => void handleMover(index, 'down')}
                          disabled={index === fila.length - 1 || (item.iniciada && !celulaSelecionada)}
                          aria-label={item.iniciada && !celulaSelecionada ? 'OP em execução — a posição não pode ser alterada.' : 'Mover para baixo'}
                          className="p-1 border rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-700"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
};
